/**
 * Write-only secrets of the settings sections (P2 contract §2.1–2.2).
 *
 * GET /settings always returns these fields as `null` and describes them in
 * `secretsMeta: { section: { field: { set, last4 } } }`. PATCH semantics:
 *   absent or ''  → the stored value is KEPT
 *   a string      → replaces it
 *   null          → clears it
 * So a form that copies the GET values and saves them back would clear
 * every secret. The rule here: a secret is sent only when the user typed a
 * new value (Replace) or explicitly chose Remove; otherwise it is left out.
 */

export const SECRET_FIELDS = {
  paymentGateway: ['stripeSecretKey', 'paypalClientSecret'],
  integration: ['slackBotToken', 'zoomApiKey'],
  notification: ['slackWebhook'],
} as const;

export type SecretSection = keyof typeof SECRET_FIELDS;
export type SecretField<S extends SecretSection = SecretSection> = (typeof SECRET_FIELDS)[S][number];

export interface SecretMeta {
  set: boolean;
  /** Only for settings.edit holders and secrets of 8+ characters. */
  last4: string | null;
}

export type SecretsMeta = Partial<Record<SecretSection, Partial<Record<string, SecretMeta>>>>;

/** What the user chose for one secret field. */
export type SecretEdit = { mode: 'keep' } | { mode: 'replace'; value: string } | { mode: 'remove' };

export const KEEP: SecretEdit = Object.freeze({ mode: 'keep' }) as SecretEdit;

export function isSecretSection(section: string): section is SecretSection {
  return Object.prototype.hasOwnProperty.call(SECRET_FIELDS, section);
}

export function secretFieldsOf(section: string): readonly string[] {
  return isSecretSection(section) ? SECRET_FIELDS[section] : [];
}

/** Every secret of a section untouched (the state a tab opens with). */
export function keepAll(section: SecretSection): Record<string, SecretEdit> {
  return Object.fromEntries(SECRET_FIELDS[section].map((f) => [f, KEEP]));
}

/** A section from GET /settings without its secret fields (they are null there and must never be echoed back). */
export function withoutSecrets<T extends Record<string, unknown>>(section: string, values: T | null | undefined): Partial<T> {
  const out: Record<string, unknown> = { ...(values ?? {}) };
  for (const field of secretFieldsOf(section)) delete out[field];
  return out as Partial<T>;
}

/** The secret's stored state; missing meta means "not set" (fail closed: never claim a value exists). */
export function secretStatus(meta: SecretsMeta | null | undefined, section: SecretSection, field: string): SecretMeta {
  const m = meta?.[section]?.[field];
  return { set: m?.set === true, last4: m?.set === true && typeof m.last4 === 'string' && m.last4 ? m.last4 : null };
}

/**
 * The PATCH body for a section: the form's non-secret values plus only
 * the secret writes the user asked for. An untouched secret, or Replace
 * with an empty input, is left out (kept). Remove sends `null`.
 */
export function buildSectionPayload(section: string, form: Record<string, unknown>, edits: Readonly<Record<string, SecretEdit | undefined>> = {}): Record<string, unknown> {
  const payload = withoutSecrets(section, form) as Record<string, unknown>;
  for (const field of secretFieldsOf(section)) {
    const edit = edits[field];
    if (!edit || edit.mode === 'keep') continue;
    if (edit.mode === 'remove') {
      payload[field] = null;
      continue;
    }
    const value = edit.value.trim();
    if (value) payload[field] = value;
  }
  return payload;
}

const LAST4_MIN_LENGTH = 8;

/**
 * Local copy after a successful save: the section keeps every secret as
 * null (secret values are never held in the store) and the meta follows
 * what was written.
 */
export function applySavedSection(
  section: string,
  current: Record<string, unknown> | null | undefined,
  sent: Record<string, unknown>,
  meta: SecretsMeta | null | undefined
): { values: Record<string, unknown>; meta: SecretsMeta } {
  const values: Record<string, unknown> = { ...(current ?? {}), ...withoutSecrets(section, sent) };
  const nextMeta: SecretsMeta = { ...(meta ?? {}) };
  if (isSecretSection(section)) {
    const sectionMeta: Partial<Record<string, SecretMeta>> = { ...(nextMeta[section] ?? {}) };
    for (const field of SECRET_FIELDS[section]) {
      values[field] = null;
      if (!(field in sent)) continue;
      const written = sent[field];
      if (written === null) sectionMeta[field] = { set: false, last4: null };
      else if (typeof written === 'string' && written) {
        sectionMeta[field] = { set: true, last4: written.length >= LAST4_MIN_LENGTH ? written.slice(-4) : null };
      }
    }
    nextMeta[section] = sectionMeta;
  }
  return { values, meta: nextMeta };
}
